import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import ConfirmModal from "../../common/ConfirmModal";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../common/DateRangeFilter";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import {
  filterStaffList,
  getInternalActiveStaffLookup,
  getActiveStudyLookup,
  dedupeStudiesById,
  clearStaffLookupCache,
} from "../../../services/lookupService";
import SiteStaffAssignmentPanel from "../../common/SiteStaffAssignmentPanel";
import SiteStudyAssignmentPanel from "../../common/SiteStudyAssignmentPanel";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import {
  sanitizeSiteAbbreviation,
  suggestSiteAbbreviation,
} from "../../../utils/siteAbbreviationUtils";
import {
  formatNhsNumberDisplay,
  sanitizeNhsNumberInput,
  sanitizeOdsCodeInput,
  sanitizePostcodeInput,
  sanitizeContactPhoneInput,
  validateContactEmail,
  validateContactName,
  validateContactPhone,
  validateOdsCode,
  validateUkPostcode,
} from "../../../utils/siteFieldValidationUtils";
import utils from "../../../utils/utils";
import "./userManagement.scss";
import "./SiteManagement.scss";

const ABBR_MAX_LENGTH = 10;
const SUGGESTION_DELAY_MS = 450;
const ADDRESS_PREVIEW_LEN = 20;
const alphaNumRegex = /^[a-zA-Z0-9]+$/;

const SITE_MGMT_FIELD_OPTIONS = [
  { key: "SiteName", label: "Site" },
  { key: "SiteType", label: "Site Type" },
  { key: "Status", label: "Status" },
  { key: "StudyCount", label: "Studies" },
  { key: "StaffCount", label: "Staff" },
  { key: "ContactName", label: "Contact Name" },
  { key: "Address", label: "Address" },
  { key: "OdsCode", label: "ODS" },
  { key: "NhsNumber", label: "NHS" },
  { key: "CreatedBy", label: "Created By" },
  { key: "SystemDateTime", label: "Creation Date & Time" },
  { key: "ContactPhone", label: "Contact Number" },
  { key: "ContactEmail", label: "Email" },
  { key: "Postcode", label: "Post Code" },
];

const DEFAULT_SITE_MGMT_COLUMNS = [
  "SiteName",
  "SiteType",
  "Status",
  "StudyCount",
  "StaffCount",
  "ContactName",
  "Address",
  "OdsCode",
  "NhsNumber",
];

const normalizeSiteType = (row) => {
  const raw = String(row?.SiteType ?? "").trim().toLowerCase();
  return raw === "hub" ? "Hub" : "Spoke";
};

const rowIsHub = (row) => normalizeSiteType(row) === "Hub";

const SITE_MGMT_EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const SITE_MGMT_COLUMN_DATE_PRESETS = DEFAULT_PRESETS;

const SITE_MGMT_FILTERS_INITIAL = {
  status: "",
  siteType: "",
  createdBy: [],
  createdDateRange: { ...SITE_MGMT_EMPTY_DATE_RANGE },
};

const getSiteAddressOnly = (row) =>
  String(row?.Address || row?.Location || "").trim();

const formatSiteDisplayName = (row) => {
  const name = String(row?.SiteName ?? "").trim();
  const abbr = String(row?.SiteAbbr ?? "").trim();
  if (abbr && name) return `(${abbr}) ${name}`;
  return abbr || name || "—";
};

const getCreatedByLabel = (row) => String(row?.CreatedBy ?? "").trim();

const rowIsActive = (row) => {
  const value = row?.IsActive;
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  return (
    String(row?.Status || "")
      .trim()
      .toLowerCase() === "active"
  );
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const matchesDateRange = (value, range) => {
  if (!range || range.preset === "All Time" || (!range.startDate && !range.endDate)) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw || raw.toUpperCase() === "N/A") return false;

  const date = utils.parseDateInput(raw);
  if (!date) return false;

  const start = range.startDate
    ? startOfDay(utils.parseDateInput(range.startDate))
    : null;
  const end = range.endDate
    ? endOfDay(utils.parseDateInput(range.endDate))
    : null;

  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

const buildCreatedByFilterOptions = (rows) => {
  const set = new Set();
  for (const row of rows || []) {
    const value = getCreatedByLabel(row);
    if (value && value !== "—") set.add(value);
  }
  return [...set]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((value) => ({ value, label: value }));
};

const applySiteMgmtFilters = (rows, filters) =>
  (rows || []).filter((row) => {
    if (filters.status === "active" && !rowIsActive(row)) return false;
    if (filters.status === "inactive" && rowIsActive(row)) return false;

    if (filters.siteType === "hub" || filters.siteType === "spoke") {
      if (normalizeSiteType(row).toLowerCase() !== filters.siteType) return false;
    }

    if (filters.createdBy?.length) {
      const createdBy = getCreatedByLabel(row);
      if (!filters.createdBy.includes(createdBy)) return false;
    }

    if (!matchesDateRange(row?.SystemDateTime, filters.createdDateRange)) {
      return false;
    }

    return true;
  });

const basePayload = (userData, overrides = {}) => ({
  SiteId: "",
  SiteName: "",
  SiteAbbr: "",
  Location: "",
  ContactName: "",
  ContactPhone: "",
  ContactEmail: "",
  Address: "",
  Postcode: "",
  OdsCode: "",
  NhsNumber: "",
  StaffId: "",
  StudyId: "",
  OrganizationId: userData?.OrganizationId || "",
  CreatedBy: userData?.StaffId || "",
  ActionType: "",
  ...overrides,
});

const normalizeListResponse = (response) => {
  const d = response?.data;
  if (Array.isArray(d?.SuccessStory)) return d.SuccessStory;
  if (Array.isArray(d)) return d;
  return [];
};

const firstResultRow = (response) => {
  const list = normalizeListResponse(response);
  return list.length ? list[0] : null;
};

const isProcSuccess = (row) =>
  row != null && (Number(row.Success) === 1 || row.Success === true);

const SiteManagement = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const showAlertRef = useRef(showAlert);

  useEffect(() => {
    showAlertRef.current = showAlert;
  }, [showAlert]);

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [siteName, setSiteName] = useState("");
  const [siteAbbr, setSiteAbbr] = useState("");
  const [odsCode, setOdsCode] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [abbrTouched, setAbbrTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    action: null,
    row: null,
  });
  const [linkedModal, setLinkedModal] = useState({
    open: false,
    type: null,
    site: null,
    items: [],
  });
  const [availableStaff, setAvailableStaff] = useState([]);
  const [availableStudies, setAvailableStudies] = useState([]);
  const [assigningStaff, setAssigningStaff] = useState(false);
  const [assigningStudy, setAssigningStudy] = useState(false);
  const [removingStaffId, setRemovingStaffId] = useState("");
  const [removingStudyId, setRemovingStudyId] = useState("");
  const [tableFilters, setTableFilters] = useState(SITE_MGMT_FILTERS_INITIAL);

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const createdByFilterOptions = useMemo(
    () => buildCreatedByFilterOptions(rows),
    [rows],
  );

  const resetForm = () => {
    setEditingRow(null);
    setSiteName("");
    setSiteAbbr("");
    setOdsCode("");
    setContactName("");
    setContactPhone("");
    setContactEmail("");
    setAddress("");
    setPostcode("");
    setAbbrTouched(false);
    setErrors({});
  };

  const fetchSites = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;

    try {
      setListLoading(true);
      const response = await api.post(
        API_ENDPOINTS.SITE.SITE_ACTIVITY,
        basePayload(userData, { ActionType: "GetAllSites" }),
      );

      if (response?.success === 1) {
        setRows(normalizeListResponse(response));
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error loading sites", error);
      showAlertRef.current({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load sites.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.OrganizationId && userData?.StaffId) {
      fetchSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  useEffect(() => {
    if (!showModal || !siteName.trim()) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (!abbrTouched) {
        setSiteAbbr(suggestSiteAbbreviation(siteName, ABBR_MAX_LENGTH));
      }
    }, SUGGESTION_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [siteName, showModal, abbrTouched]);

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setSiteName(row.SiteName || "");
    setSiteAbbr(row.SiteAbbr || "");
    setOdsCode(row.OdsCode || "");
    setContactName(row.ContactName || "");
    setContactPhone(row.ContactPhone || "");
    setContactEmail(row.ContactEmail || "");
    setAddress(row.Address || row.Location || "");
    setPostcode(row.Postcode || "");
    setAbbrTouched(true);
    setErrors({});
    setShowModal(true);
  };

  const isHubReadOnly = Boolean(editingRow && rowIsHub(editingRow));

  const handleSiteNameChange = (value) => {
    setSiteName(value);
  };

  const handleSiteAbbrChange = (value) => {
    setAbbrTouched(true);
    setSiteAbbr(sanitizeSiteAbbreviation(value, ABBR_MAX_LENGTH));
  };

  const handlePostcodeChange = (value) => {
    setPostcode(sanitizePostcodeInput(value));
    setErrors((prev) => {
      if (!prev.postcode) return prev;
      const next = { ...prev };
      delete next.postcode;
      return next;
    });
  };

  const handleContactPhoneChange = (value) => {
    setContactPhone(sanitizeContactPhoneInput(value));
    setErrors((prev) => {
      if (!prev.contactPhone) return prev;
      const next = { ...prev };
      delete next.contactPhone;
      return next;
    });
  };

  const handleOdsCodeChange = (value) => {
    setOdsCode(sanitizeOdsCodeInput(value));
    setErrors((prev) => {
      if (!prev.odsCode) return prev;
      const next = { ...prev };
      delete next.odsCode;
      return next;
    });
  };

  const validate = () => {
    const next = {};
    const trimmedName = siteName.trim();
    const trimmedAbbr = siteAbbr.trim().toUpperCase();
    const currentSiteId = editingRow?.SiteId || "";
    const trimmedAddress = address.trim();

    if (!trimmedName) next.siteName = "Site name is required.";
    else if (trimmedName.length > 150)
      next.siteName = "Maximum 150 characters allowed.";
    else if (
      rows.some(
        (row) =>
          row.SiteId !== currentSiteId &&
          rowIsActive(row) &&
          String(row.SiteName || "").trim().toLowerCase() ===
            trimmedName.toLowerCase(),
      )
    ) {
      next.siteName = "Site name already exists.";
    }

    if (!siteAbbr.trim()) next.siteAbbr = "Site abbreviation is required.";
    else if (!alphaNumRegex.test(siteAbbr.trim()))
      next.siteAbbr = "Only letters and numbers are allowed.";
    else if (siteAbbr.trim().length > ABBR_MAX_LENGTH)
      next.siteAbbr = `Maximum ${ABBR_MAX_LENGTH} characters allowed.`;
    else if (
      rows.some(
        (row) =>
          row.SiteId !== currentSiteId &&
          rowIsActive(row) &&
          String(row.SiteAbbr || "").trim().toUpperCase() === trimmedAbbr,
      )
    ) {
      next.siteAbbr = "Site abbreviation already exists.";
    }

    const odsResult = validateOdsCode(odsCode, { required: false });
    if (!odsResult.valid) next.odsCode = odsResult.message;
    else if (
      odsResult.code &&
      rows.some(
        (row) =>
          row.SiteId !== currentSiteId &&
          rowIsActive(row) &&
          String(row.OdsCode || "").trim().toUpperCase() === odsResult.code,
      )
    ) {
      next.odsCode = "ODS code already exists.";
    }

    const nameResult = validateContactName(contactName, { required: false });
    if (!nameResult.valid) next.contactName = nameResult.message;

    const phoneResult = validateContactPhone(contactPhone, { required: false });
    if (!phoneResult.valid) next.contactPhone = phoneResult.message;

    const emailResult = validateContactEmail(contactEmail, { required: false });
    if (!emailResult.valid) next.contactEmail = emailResult.message;

    if (trimmedAddress.length > 500)
      next.address = "Maximum 500 characters allowed.";

    const postcodeResult = validateUkPostcode(postcode, { required: false });
    if (!postcodeResult.valid) next.postcode = postcodeResult.message;

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (isHubReadOnly) {
      showAlert({
        title: "Hub site",
        message: "Hub site is read-only and cannot be edited.",
        type: "warning",
      });
      return;
    }
    if (!validate() || !userData?.OrganizationId || !userData?.StaffId) return;

    const trimmedAddress = address.trim();
    const odsResult = validateOdsCode(odsCode, { required: false });
    const nameResult = validateContactName(contactName, { required: false });
    const phoneResult = validateContactPhone(contactPhone, { required: false });
    const emailResult = validateContactEmail(contactEmail, { required: false });
    const postcodeResult = validateUkPostcode(postcode, { required: false });

    const body = basePayload(userData, {
      ActionType: editingRow ? "UpdateSite" : "SaveSite",
      SiteId: editingRow?.SiteId || "",
      SiteName: siteName.trim(),
      SiteAbbr: siteAbbr.trim().toUpperCase(),
      Location: trimmedAddress,
      ContactName: nameResult.name || "",
      ContactPhone: phoneResult.phone || "",
      ContactEmail: emailResult.email || "",
      Address: trimmedAddress,
      Postcode: postcodeResult.postcode || "",
      OdsCode: odsResult.code || "",
      NhsNumber: editingRow
        ? sanitizeNhsNumberInput(editingRow.NhsNumber)
        : "",
    });

    try {
      showLoader(editingRow ? "Updating site..." : "Saving site...");
      const response = await api.post(API_ENDPOINTS.SITE.SITE_ACTIVITY, body);
      const row = firstResultRow(response);

      if (isProcSuccess(row)) {
        showAlert({
          title: "Success",
          message:
            row?.MSG ||
            (editingRow
              ? "Site has been updated successfully."
              : "Site has been saved successfully."),
          type: "success",
        });
        setShowModal(false);
        resetForm();
        await fetchSites();
      } else {
        const message = row?.MSG || "Could not save site.";
        const nextErrors = {};

        if (/site name already exists/i.test(message)) {
          nextErrors.siteName = "Site name already exists.";
        }
        if (/site abbreviation already exists/i.test(message)) {
          nextErrors.siteAbbr = "Site abbreviation already exists.";
        }
        if (/ods code already exists/i.test(message)) {
          nextErrors.odsCode = "ODS code already exists.";
        }

        if (Object.keys(nextErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...nextErrors }));
        }

        showAlert({
          title: "Error",
          message,
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error saving site", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save site.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleToggleStatusClick = (row) => {
    if (rowIsHub(row)) {
      showAlert({
        title: "Not allowed",
        message:
          "Hub site cannot be deactivated. Only Spoke sites can be activated or deactivated.",
        type: "warning",
      });
      return;
    }
    const action = rowIsActive(row) ? "deactivate" : "activate";
    setConfirmModal({ open: true, action, row });
  };

  const handleToggleConfirm = async () => {
    const { action, row } = confirmModal;
    setConfirmModal({ open: false, action: null, row: null });
    if (!row || !userData?.OrganizationId || !userData?.StaffId) return;

    const authRes = await openAuthModal({ showDescription: false });
    if (!authRes || authRes.Success !== 1) return;

    try {
      showLoader(
        action === "deactivate" ? "Deactivating site..." : "Activating site...",
      );
      const response = await api.post(
        API_ENDPOINTS.SITE.SITE_ACTIVITY,
        basePayload(userData, {
          ActionType: "ToggleSiteStatus",
          SiteId: row.SiteId,
        }),
      );
      const result = firstResultRow(response);

      if (isProcSuccess(result)) {
        showAlert({
          title: "Success",
          message:
            result?.MSG ||
            (action === "deactivate"
              ? "Site has been deactivated successfully."
              : "Site has been activated successfully."),
          type: "success",
        });
        await fetchSites();
      } else {
        showAlert({
          title: "Error",
          message: result?.MSG || "Failed to update site status.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error updating site status", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update site status.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const loadSiteStaffModal = async (row) => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;

    try {
      showLoader("Loading staff...");
      const staffResponse = await api.post(
        API_ENDPOINTS.SITE.SITE_ACTIVITY,
        basePayload(userData, {
          ActionType: "GetSiteStaff",
          SiteId: row.SiteId,
        }),
      );

      const rawItems =
        staffResponse?.success === 1
          ? normalizeListResponse(staffResponse)
          : [];

      const items = filterStaffList(rawItems, {
        activeOnly: false,
        excludeSystemAdmin: true,
      });

      const siteIsActive = rowIsActive(row);
      const nextAvailable = siteIsActive
        ? await getInternalActiveStaffLookup(userData, {
            excludeStaffIds: items.map((item) => item.StaffId),
          })
        : [];

      setAvailableStaff(nextAvailable);
      setLinkedModal({
        open: true,
        type: "staff",
        site: row,
        items,
      });
    } catch (error) {
      console.error("Error loading site staff", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load site staff.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const loadSiteStudiesModal = async (row) => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;

    try {
      showLoader("Loading studies...");
      const studiesResponse = await api.post(
        API_ENDPOINTS.SITE.SITE_ACTIVITY,
        basePayload(userData, {
          ActionType: "GetSiteStudies",
          SiteId: row.SiteId,
        }),
      );

      const items = dedupeStudiesById(
        studiesResponse?.success === 1
          ? normalizeListResponse(studiesResponse)
          : [],
      );

      const siteIsActive = rowIsActive(row);
      const nextAvailable = siteIsActive
        ? await getActiveStudyLookup(userData, {
            excludeStudyIds: items.map((item) => item.StudyId || item.studyId || item.StudyID),
          })
        : [];

      setAvailableStudies(nextAvailable);
      setLinkedModal({
        open: true,
        type: "studies",
        site: row,
        items,
      });
    } catch (error) {
      console.error("Error loading site studies", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load site studies.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const openLinkedModal = async (row, type) => {
    if (type === "staff") {
      await loadSiteStaffModal(row);
      return;
    }

    if (type === "studies") {
      await loadSiteStudiesModal(row);
    }
  };

  const assignStaffToSite = async (staffIds) => {
    const site = linkedModal.site;
    const ids = [...new Set((staffIds || []).filter(Boolean))];

    if (!site?.SiteId || ids.length === 0 || !userData?.OrganizationId || !userData?.StaffId) {
      showAlert({
        title: "Add staff",
        message: "Please select at least one staff member to add.",
        type: "warning",
      });
      return;
    }

    if (!rowIsActive(site)) {
      showAlert({
        title: "Site inactive",
        message: "Staff cannot be added while this site is inactive.",
        type: "warning",
      });
      return;
    }

    try {
      setAssigningStaff(true);
      showLoader(ids.length > 1 ? "Assigning staff..." : "Assigning staff...");

      let successCount = 0;
      let lastError = "";

      for (const staffId of ids) {
        const response = await api.post(
          API_ENDPOINTS.SITE.SITE_ACTIVITY,
          basePayload(userData, {
            ActionType: "AssignStaffToSite",
            SiteId: site.SiteId,
            StaffId: staffId,
          }),
        );
        const result = firstResultRow(response);
        if (isProcSuccess(result)) {
          successCount += 1;
        } else {
          lastError = result?.MSG || "Failed to assign staff to site.";
        }
      }

      if (successCount > 0) {
        showAlert({
          title: "Success",
          message:
            successCount === 1
              ? "Staff has been assigned to site successfully."
              : `${successCount} staff members have been assigned to this site.`,
          type: "success",
        });
        clearStaffLookupCache();
        await loadSiteStaffModal(site);
        await fetchSites();
      }

      if (lastError) {
        showAlert({
          title: successCount > 0 ? "Partial success" : "Error",
          message: lastError,
          type: successCount > 0 ? "warning" : "danger",
        });
      }
    } catch (error) {
      console.error("Error assigning staff to site", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to assign staff to site.",
        type: "danger",
      });
    } finally {
      setAssigningStaff(false);
      hideLoader();
    }
  };

  const handleRemoveStaff = async (staffId) => {
    const site = linkedModal.site;
    if (!site?.SiteId || !staffId || !userData?.OrganizationId || !userData?.StaffId) {
      return;
    }

    if (!rowIsActive(site)) {
      showAlert({
        title: "Site inactive",
        message: "Staff cannot be removed while this site is inactive.",
        type: "warning",
      });
      return;
    }

    try {
      setRemovingStaffId(staffId);
      showLoader("Removing staff...");
      const response = await api.post(
        API_ENDPOINTS.SITE.SITE_ACTIVITY,
        basePayload(userData, {
          ActionType: "RemoveStaffFromSite",
          SiteId: site.SiteId,
          StaffId: staffId,
        }),
      );
      const result = firstResultRow(response);

      if (isProcSuccess(result)) {
        showAlert({
          title: "Success",
          message: result?.MSG || "Staff has been removed from site successfully.",
          type: "success",
        });
        clearStaffLookupCache();
        await loadSiteStaffModal(site);
        await fetchSites();
      } else {
        showAlert({
          title: "Error",
          message: result?.MSG || "Failed to remove staff from site.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error removing staff from site", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to remove staff from site.",
        type: "danger",
      });
    } finally {
      setRemovingStaffId("");
      hideLoader();
    }
  };

  const assignStudyToSite = async (studyIds) => {
    const site = linkedModal.site;
    const ids = [...new Set((studyIds || []).filter(Boolean))];

    if (!site?.SiteId || ids.length === 0 || !userData?.OrganizationId || !userData?.StaffId) {
      showAlert({
        title: "Add studies",
        message: "Please select at least one study to add.",
        type: "warning",
      });
      return;
    }

    if (!rowIsActive(site)) {
      showAlert({
        title: "Site inactive",
        message: "Studies cannot be added while this site is inactive.",
        type: "warning",
      });
      return;
    }

    try {
      setAssigningStudy(true);
      showLoader(ids.length > 1 ? "Assigning studies..." : "Assigning study...");

      let successCount = 0;
      let lastError = "";

      for (const studyId of ids) {
        const response = await api.post(
          API_ENDPOINTS.SITE.SITE_ACTIVITY,
          basePayload(userData, {
            ActionType: "AssignStudyToSite",
            SiteId: site.SiteId,
            StudyId: studyId,
            StaffId: studyId,
          }),
        );
        const result = firstResultRow(response);
        if (isProcSuccess(result)) {
          successCount += 1;
        } else {
          lastError = result?.MSG || "Failed to assign study to site.";
        }
      }

      if (successCount > 0) {
        showAlert({
          title: "Success",
          message:
            successCount === 1
              ? "Study has been assigned to site successfully."
              : `${successCount} studies have been assigned to this site.`,
          type: "success",
        });
        await loadSiteStudiesModal(site);
        await fetchSites();
      }

      if (lastError) {
        showAlert({
          title: successCount > 0 ? "Partial success" : "Error",
          message: lastError,
          type: successCount > 0 ? "warning" : "danger",
        });
      }
    } catch (error) {
      console.error("Error assigning study to site", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to assign study to site.",
        type: "danger",
      });
    } finally {
      setAssigningStudy(false);
      hideLoader();
    }
  };

  const handleRemoveStudy = async (studyId) => {
    const site = linkedModal.site;
    if (!site?.SiteId || !studyId || !userData?.OrganizationId || !userData?.StaffId) {
      return;
    }

    if (!rowIsActive(site)) {
      showAlert({
        title: "Site inactive",
        message: "Studies cannot be removed while this site is inactive.",
        type: "warning",
      });
      return;
    }

    try {
      setRemovingStudyId(studyId);
      showLoader("Removing study...");
      const response = await api.post(
        API_ENDPOINTS.SITE.SITE_ACTIVITY,
        basePayload(userData, {
          ActionType: "RemoveStudyFromSite",
          SiteId: site.SiteId,
          StudyId: studyId,
          StaffId: studyId,
        }),
      );
      const result = firstResultRow(response);

      if (isProcSuccess(result)) {
        showAlert({
          title: "Success",
          message: result?.MSG || "Study has been removed from site successfully.",
          type: "success",
        });
        await loadSiteStudiesModal(site);
        await fetchSites();
      } else {
        showAlert({
          title: "Error",
          message: result?.MSG || "Failed to remove study from site.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error removing study from site", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to remove study from site.",
        type: "danger",
      });
    } finally {
      setRemovingStudyId("");
      hideLoader();
    }
  };

  const closeLinkedModal = () => {
    setLinkedModal({ open: false, type: null, site: null, items: [] });
    setAvailableStaff([]);
    setAvailableStudies([]);
  };

  const filteredRows = useMemo(
    () => applySiteMgmtFilters(rows, tableFilters),
    [rows, tableFilters],
  );

  const getSiteMgmtFieldLabel = useCallback(
    (field) =>
      SITE_MGMT_FIELD_OPTIONS.find((option) => option.key === field)?.label ||
      field,
    [],
  );

  const renderReportCell = (row, field) => {
    if (field === "SiteName") {
      const name = String(row?.SiteName ?? "").trim() || "—";
      const abbr = String(row?.SiteAbbr ?? "").trim();

      return (
        <span
          className="site-mgmt-report__name-cell"
          title={formatSiteDisplayName(row)}
        >
          {abbr ? (
            <span className="site-mgmt-report__abbr-badge">{abbr}</span>
          ) : null}
          <button
            type="button"
            className="report-table__cell-link site-mgmt-report__name-link"
            onClick={() => openEditModal(row)}
          >
            {name}
          </button>
        </span>
      );
    }

    if (field === "Address") {
      const text = getSiteAddressOnly(row);
      if (!text) return "—";
      if (text.length <= ADDRESS_PREVIEW_LEN) return text;
      return (
        <HoverPreview
          content={text}
          previewLength={ADDRESS_PREVIEW_LEN}
          appendEllipsis
          className="site-mgmt-report__address-text"
        />
      );
    }

    if (field === "Postcode") {
      return String(row.Postcode || "").trim() || "—";
    }

    if (field === "ContactPhone") {
      return String(row.ContactPhone || "").trim() || "—";
    }

    if (field === "ContactEmail") {
      return String(row.ContactEmail || "").trim() || "—";
    }

    if (field === "NhsNumber") {
      const digits = sanitizeNhsNumberInput(row.NhsNumber);
      return digits ? formatNhsNumberDisplay(digits) : "—";
    }

    if (field === "StudyCount") {
      const count = row.StudyCount ?? 0;
      return (
        <button
          type="button"
          className="report-table__cell-link site-mgmt-report__count-link"
          onClick={() => openLinkedModal(row, "studies")}
          title="View studies"
          aria-label={`View studies for ${row.SiteName || "site"}`}
        >
          {count}
        </button>
      );
    }

    if (field === "StaffCount") {
      const count = row.StaffCount ?? 0;
      return (
        <button
          type="button"
          className="report-table__cell-link site-mgmt-report__count-link"
          onClick={() => openLinkedModal(row, "staff")}
          title="View staff"
          aria-label={`View staff for ${row.SiteName || "site"}`}
        >
          {count}
        </button>
      );
    }

    if (field === "SiteType") {
      const siteType = normalizeSiteType(row);
      return (
        <span
          className={`site-mgmt-report__type-badge is-${siteType.toLowerCase()}`}
          title={siteType === "Hub" ? "Organisation Hub site" : "Spoke site"}
        >
          {siteType}
        </span>
      );
    }

    if (field === "Status") {
      const active = rowIsActive(row);
      const hub = rowIsHub(row);
      return (
        <button
          type="button"
          className={`site-mgmt-report__status-btn is-${active ? "active" : "inactive"}${
            hub ? " is-hub-locked" : ""
          }`}
          onClick={() => handleToggleStatusClick(row)}
          title={
            hub
              ? "Hub site cannot be deactivated"
              : active
                ? "Deactivate site"
                : "Activate site"
          }
          aria-label={
            hub
              ? `Hub site ${row.SiteName || ""} cannot be deactivated`
              : active
                ? `Deactivate ${row.SiteName || "site"}`
                : `Activate ${row.SiteName || "site"}`
          }
        >
          <i
            className={`fas ${active ? "fa-check-circle" : "fa-ban"}`}
            aria-hidden="true"
          />
          <span>{active ? "Active" : "Inactive"}</span>
          {!hub ? (
            <i
              className={`fas site-mgmt-report__status-action-icon ${
                active ? "fa-ban" : "fa-check-circle"
              }`}
              aria-hidden="true"
            />
          ) : null}
        </button>
      );
    }

    if (field === "SystemDateTime") {
      return utils.formatDateTimeDisplay(row.SystemDateTime);
    }

    const value = row[field];
    if (value == null || value === "") return "—";
    return String(value);
  };

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;

    if (
      field === "Status" ||
      field === "SiteType" ||
      field === "CreatedBy" ||
      field === "SystemDateTime"
    ) {
      return true;
    }

    if (field === "SiteName") {
      return (
        formatSiteDisplayName(row).toLowerCase().includes(term) ||
        String(row.SiteName || "")
          .toLowerCase()
          .includes(term) ||
        String(row.SiteAbbr || "")
          .toLowerCase()
          .includes(term)
      );
    }

    if (field === "Address") {
      return getSiteAddressOnly(row).toLowerCase().includes(term);
    }

    if (field === "NhsNumber") {
      return sanitizeNhsNumberInput(row.NhsNumber).includes(
        term.replace(/[\s-]/g, ""),
      );
    }

    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "Status") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search site-mgmt-report__column-select"
            value={tableFilters.status}
            disabled={options?.disabled}
            onChange={(event) =>
              updateTableFilters({ status: event.target.value })
            }
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        );
      }

      if (field === "SiteType") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search site-mgmt-report__column-select"
            value={tableFilters.siteType}
            disabled={options?.disabled}
            onChange={(event) =>
              updateTableFilters({ siteType: event.target.value })
            }
          >
            <option value="">All</option>
            <option value="hub">Hub</option>
            <option value="spoke">Spoke</option>
          </select>
        );
      }

      if (field === "CreatedBy") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={createdByFilterOptions}
            value={tableFilters.createdBy}
            onChange={(values) => updateTableFilters({ createdBy: values })}
            emptyLabel="All creators"
            addLabel="+"
            popoverTitle="Filter by created by"
            popoverSubtitle="Select one or more creators"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "SystemDateTime") {
        if (options?.checkOnly) return true;
        return (
          <DateRangeFilter
            value={tableFilters.createdDateRange}
            onChange={(range) =>
              updateTableFilters({ createdDateRange: range })
            }
            placeholder="Creation date range"
            presetOptions={SITE_MGMT_COLUMN_DATE_PRESETS}
            compact
            disabled={options?.disabled}
          />
        );
      }

      return undefined;
    },
    [tableFilters, createdByFilterOptions, updateTableFilters],
  );

  const layoutStorageKey = useMemo(
    () => `site-mgmt-report-v5-${userData?.StaffId || "anon"}`,
    [userData?.StaffId],
  );

  const linkedSiteIsReadOnly =
    linkedModal.open && linkedModal.site && !rowIsActive(linkedModal.site);

  return (
    <div className="user-mgmt-page site-mgmt-page site-mgmt-list-page">
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Site Management" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: fetchSites,
          },
          {
            icon: "fas fa-plus",
            label: "Add Site",
            onClick: openAddModal,
          },
        ]}
      />

      <div className="site-mgmt-report-wrap">
        <ReportTable
          rows={filteredRows}
          fieldOptions={SITE_MGMT_FIELD_OPTIONS}
          defaultColumns={DEFAULT_SITE_MGMT_COLUMNS}
          getFieldLabel={getSiteMgmtFieldLabel}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          frozenColumnWidthRem={16}
          hideColumnHeaderActionsFields={[
            "Status",
            "CreatedBy",
            "SystemDateTime",
          ]}
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() =>
          setConfirmModal({ open: false, action: null, row: null })
        }
        onConfirm={handleToggleConfirm}
        title={
          confirmModal.action === "deactivate"
            ? "Deactivate site"
            : "Activate site"
        }
        message={
          confirmModal.row
            ? confirmModal.action === "deactivate"
              ? `Are you sure you want to deactivate site "${confirmModal.row.SiteName}"?`
              : `Are you sure you want to activate site "${confirmModal.row.SiteName}"?`
            : ""
        }
        confirmText="Continue"
        cancelText="Cancel"
        confirmButtonClass={
          confirmModal.action === "deactivate" ? "btn-danger" : "btn-success"
        }
      />

      {linkedModal.open && (
        <div
          className="modal fade show d-block site-link-modal-backdrop"
          tabIndex="-1"
          style={{ zIndex: 1100 }}
        >
          <div
            className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${
              linkedModal.type === "staff" || linkedModal.type === "studies"
                ? "modal-xl site-staff-modal-dialog"
                : "modal-lg"
            }`}
          >
            <div className="modal-content site-link-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  {linkedModal.type === "studies" ? "Studies" : "Staff"} —{" "}
                  {linkedModal.site?.SiteName || ""}
                  {linkedSiteIsReadOnly ? " (View only)" : ""}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeLinkedModal}
                ></button>
              </div>

              <div className="site-link-modal__body">
                {linkedSiteIsReadOnly && (
                  <div className="site-link-modal__read-only-note">
                    This site is inactive. Linked{" "}
                    {linkedModal.type === "staff" ? "staff" : "studies"} can be viewed only.
                  </div>
                )}
                {linkedModal.type === "staff" ? (
                  <SiteStaffAssignmentPanel
                    availableStaff={availableStaff}
                    assignedItems={linkedModal.items}
                    onAssignSelected={assignStaffToSite}
                    onAssignOne={(staffId) => assignStaffToSite([staffId])}
                    onRemoveStaff={handleRemoveStaff}
                    assigningStaff={assigningStaff}
                    removingStaffId={removingStaffId}
                    readOnly={linkedSiteIsReadOnly}
                  />
                ) : (
                  <SiteStudyAssignmentPanel
                    availableStudies={availableStudies}
                    assignedItems={linkedModal.items}
                    onAssignSelected={assignStudyToSite}
                    onAssignOne={(studyId) => assignStudyToSite([studyId])}
                    onRemoveStudy={handleRemoveStudy}
                    assigningStudy={assigningStudy}
                    removingStudyId={removingStudyId}
                    readOnly={linkedSiteIsReadOnly}
                  />
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeLinkedModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="modal fade show d-block site-mgmt-form-modal-backdrop"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable site-mgmt-form-modal-dialog">
            <div className="modal-content site-mgmt-form-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isHubReadOnly
                    ? "View Hub Site"
                    : editingRow
                      ? "Update Site"
                      : "Add Site"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {isHubReadOnly ? (
                  <div className="alert alert-info py-2 px-3 mb-3" role="status">
                    Hub site is read-only. It cannot be edited or deactivated.
                  </div>
                ) : null}
                <fieldset disabled={isHubReadOnly} className="site-mgmt-form-fieldset">
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label">
                      Site Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.siteName ? "is-invalid" : ""}`}
                      value={siteName}
                      onChange={(e) => handleSiteNameChange(e.target.value)}
                      maxLength={150}
                      placeholder="Site name"
                      readOnly={isHubReadOnly}
                    />
                    {errors.siteName && (
                      <div className="invalid-feedback">{errors.siteName}</div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Site Abbreviation <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.siteAbbr ? "is-invalid" : ""}`}
                      value={siteAbbr}
                      onChange={(e) => handleSiteAbbrChange(e.target.value)}
                      maxLength={ABBR_MAX_LENGTH}
                      placeholder="e.g. LRC"
                      readOnly={isHubReadOnly}
                    />
                    {errors.siteAbbr && (
                      <div className="invalid-feedback">{errors.siteAbbr}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">ODS Code</label>
                    <input
                      type="text"
                      className={`form-control ${errors.odsCode ? "is-invalid" : ""}`}
                      value={odsCode}
                      onChange={(e) => handleOdsCodeChange(e.target.value)}
                      maxLength={8}
                      placeholder="e.g. RYJ"
                      autoComplete="off"
                      readOnly={isHubReadOnly}
                    />
                    {errors.odsCode ? (
                      <div className="invalid-feedback">{errors.odsCode}</div>
                    ) : (
                      <div className="form-text">
                        Optional. 3–8 alphanumeric characters (A–Z, 0–9)
                      </div>
                    )}
                  </div>
                </div>

                <hr className="my-4" />
                <h6 className="mb-3 text-secondary">Site contact details</h6>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Contact Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.contactName ? "is-invalid" : ""}`}
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      maxLength={150}
                      placeholder="Primary contact name"
                      readOnly={isHubReadOnly}
                    />
                    {errors.contactName && (
                      <div className="invalid-feedback">{errors.contactName}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className={`form-control ${errors.contactPhone ? "is-invalid" : ""}`}
                      value={contactPhone}
                      onChange={(e) => handleContactPhoneChange(e.target.value)}
                      maxLength={20}
                      placeholder="e.g. 020 7946 0958"
                      readOnly={isHubReadOnly}
                    />
                    {errors.contactPhone && (
                      <div className="invalid-feedback">{errors.contactPhone}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className={`form-control ${errors.contactEmail ? "is-invalid" : ""}`}
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      maxLength={200}
                      placeholder="contact@example.com"
                      readOnly={isHubReadOnly}
                    />
                    {errors.contactEmail && (
                      <div className="invalid-feedback">{errors.contactEmail}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Postcode</label>
                    <input
                      type="text"
                      className={`form-control ${errors.postcode ? "is-invalid" : ""}`}
                      value={postcode}
                      onChange={(e) => handlePostcodeChange(e.target.value)}
                      maxLength={8}
                      placeholder="e.g. SW1A 1AA"
                      readOnly={isHubReadOnly}
                    />
                    {errors.postcode && (
                      <div className="invalid-feedback">{errors.postcode}</div>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Address</label>
                    <textarea
                      className={`form-control ${errors.address ? "is-invalid" : ""}`}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      maxLength={500}
                      rows={2}
                      placeholder="Site address"
                      readOnly={isHubReadOnly}
                    />
                    {errors.address && (
                      <div className="invalid-feedback">{errors.address}</div>
                    )}
                  </div>
                </div>
                </fieldset>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  {isHubReadOnly ? "Close" : "Cancel"}
                </button>
                {!isHubReadOnly ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                  >
                    {editingRow ? "Update" : "Save"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteManagement;
